import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Chip, Skeleton } from '@mui/material';
import { useCallback, useRef, useState, type KeyboardEvent } from 'react';

import { useMentionUser } from '../hooks/useMentionUser';
import { MentionHoverCard } from '../MentionHoverCard';
import type { MentionChipProps } from './types';

export function MentionChip({
  userId,
  displayName,
  email,
  role,
  department,
  avatar,
  isDeactivated,
  permissionStatus,
  entityId,
  disableAutoFetch = false,
  onClick,
  sx,
}: MentionChipProps) {
  const [hoverCardOpen, setHoverCardOpen] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const chipRef = useRef<HTMLDivElement>(null);

  // Only trigger auto-fetch if enabled. Passing undefined to useMentionUser
  // makes it a no-op so we don't make unnecessary requests.
  const shouldFetch = !disableAutoFetch;
  const { user: fetchedUser, isLoading } = useMentionUser(
    shouldFetch ? userId : undefined,
    entityId,
  );

  // Props take precedence over fetched data — callers that already have a
  // name (e.g. from a persisted mention) see it instantly; fetched data
  // fills in the gaps (role, department, avatar, deactivated status).
  const resolvedDisplayName =
    displayName && displayName.length > 0 ? displayName : (fetchedUser?.fullName ?? '');
  const resolvedRole = role ?? fetchedUser?.role.displayName;
  const resolvedDepartment = department ?? fetchedUser?.department ?? undefined;
  const resolvedAvatar = avatar !== undefined ? avatar : fetchedUser?.avatar;
  const resolvedIsDeactivated = isDeactivated ?? fetchedUser?.disabled ?? false;
  const resolvedEmail = email ?? fetchedUser?.email;

  const showCard = useCallback(() => {
    clearTimeout(hideTimer.current);
    setHoverCardOpen(true);
  }, []);

  const hideCard = useCallback(() => {
    hideTimer.current = setTimeout(() => setHoverCardOpen(false), 200);
  }, []);

  const handleClick = useCallback(() => onClick?.(userId), [onClick, userId]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setHoverCardOpen((prev) => !prev);
    } else if (e.key === 'Escape') {
      setHoverCardOpen(false);
    }
  }, []);

  // Skeleton state: show a placeholder only when we have no name to show yet
  // AND we're actively fetching. This prevents a flicker when callers already
  // supply a displayName.
  const showSkeleton = isLoading && resolvedDisplayName.length === 0;

  if (showSkeleton) {
    return (
      <Skeleton
        variant="rounded"
        width={120}
        height={24}
        sx={{ display: 'inline-block', borderRadius: 12 }}
        aria-label="Loading mentioned user"
      />
    );
  }

  const label = resolvedIsDeactivated
    ? `${resolvedDisplayName} (Deactivated)`
    : resolvedDisplayName;
  const ariaLabel = resolvedIsDeactivated
    ? `Mentioned user: ${resolvedDisplayName} (Deactivated)`
    : `Mentioned user: ${resolvedDisplayName}`;

  const icon =
    permissionStatus === 'warned' ? (
      <WarningAmberIcon fontSize="small" />
    ) : permissionStatus === 'blocked' ? (
      <BlockIcon fontSize="small" />
    ) : null;

  return (
    <>
      <Chip
        ref={chipRef}
        label={`@${label}`}
        {...(icon ? { icon } : {})}
        size="small"
        variant="filled"
        aria-label={ariaLabel}
        onClick={handleClick}
        onMouseEnter={showCard}
        onMouseLeave={hideCard}
        onFocus={showCard}
        onBlur={hideCard}
        onKeyDown={handleKeyDown}
        sx={[
          {
            cursor: 'pointer',
            fontWeight: 500,
            bgcolor: 'primary.light',
            color: 'primary.dark',
            '&:hover': { bgcolor: 'primary.200' },
            '&:focus-visible': {
              outline: 2,
              outlineStyle: 'solid',
              outlineColor: 'primary.main',
              outlineOffset: 1,
            },
            ...(resolvedIsDeactivated && {
              bgcolor: 'action.disabledBackground',
              color: 'text.disabled',
              textDecoration: 'line-through',
            }),
            ...(permissionStatus === 'blocked' && {
              bgcolor: 'action.disabledBackground',
              color: 'text.disabled',
            }),
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      />
      {chipRef.current && (
        <MentionHoverCard
          userId={userId}
          displayName={resolvedDisplayName}
          {...(resolvedEmail !== undefined ? { email: resolvedEmail } : {})}
          {...(resolvedRole !== undefined ? { role: resolvedRole } : {})}
          {...(resolvedDepartment !== undefined ? { department: resolvedDepartment } : {})}
          {...(resolvedAvatar !== undefined ? { avatar: resolvedAvatar } : {})}
          isDeactivated={resolvedIsDeactivated}
          entityId={entityId}
          // The chip has already fetched details; don't re-fetch in the hover card.
          disableAutoFetch
          anchorEl={chipRef.current}
          open={hoverCardOpen}
          onClose={() => setHoverCardOpen(false)}
        />
      )}
    </>
  );
}
